import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { basisLabel, fraction, fromNet, rateFor, type TaxRate, toCents, toNet, toNumber } from "../../shared/money";
import type { Period } from "../../shared/periods";
import { api } from "../../web/lib/api";
import { useCompany } from "../../web/lib/company";
import { useSm8Query } from "../../web/lib/queries";
import { inOrder, whileSaving } from "../../web/lib/saving";
import { type Change, flushChanges, queueChange } from "./liveRows";
import type {
    BundleLine,
    BundleRow,
    ChangeRow,
    CostSource,
    GroupStats,
    GroupTax,
    GroupTaxBasis,
    ItemStats,
    JobTimePreview,
    JobTimeSettings,
    KitPart,
    MaterialPatch,
    MaterialRow,
    MaterialUpdate,
    PriceHistoryEntry,
    PriceListAction,
    PriceListData,
    PriceListSettings,
    PricingGroupRow,
    PricingRule,
    SupplierDetails,
    SupplierPatch,
    StatsResponse,
    SupplierPriceRow,
    SupplierRow,
    SupplierStats,
    TaxQuote,
    TrackedObject,
} from "./meta";
import { pricedByGroup, withShortNames } from "./meta";
import { applyNameChange, displayName, type GroupTags, groupTagsName, type NameTagConfig, type OwnTags } from "./names";
import { costWithLabour, type ItemRate, kitCost, kitPartsPrice, missingCost, needsCheck, partItem, samePrice } from "./pricing";
import { byRealCost, costFromQuoted, costPrice, itemCost, quotedPrice, quoteOf, shownTypedCost } from "./supplierPricing";

/** How many item names a name tag change is updating (in the background). */
export interface NameUpdates {
    updating: number;
}

/**
 * Everything for the Price List, kept up to date live (from anyone, or ServiceM8): record by record
 * for items, suppliers and bundles (liveRows.ts), whole for anything else (e.g. tax rates).
 */
export function usePriceList() {
    const company = useCompany();
    const qc = useQueryClient();
    return useSm8Query<PriceListData>({
        companyId: company.id,
        objects: ["material", "materialbundle", "taxrate", "supplier", "vendor"],
        key: ["pricelist"],
        path: "/tools/pricelist",
        fresh: true,
        onChange: (change) => queueChange(qc, company.id, change),
    });
}

/** Whether this person can do `action` (PRICELIST_ACTIONS): their role allows it (Settings › Access) and ServiceM8 is connected. */
export const can = (data: Pick<PriceListData, "whyNot">, action: PriceListAction) => data.whyNot[action] === null;

/** Why they can't do `action`, for a notice, or null if they can. */
export const whyNot = (data: Pick<PriceListData, "whyNot">, action: PriceListAction) => data.whyNot[action];

/** A record's history (history.ts): its changes here and in ServiceM8, newest first. */
export function useHistory(object: TrackedObject, uuid: string) {
    const company = useCompany();
    return useSm8Query<ChangeRow[]>({ companyId: company.id, objects: [object, "staff"], key: ["pricelist", "history", object, uuid], path: `/tools/pricelist/history/${object}/${uuid}` });
}

/** Stats (stats.ts) are worked out from jobs and their lines, and items. */
const STATS_OBJECTS = ["job", "jobmaterial", "jobactivity", "material"] as const;

/** Stats for a period (days both included), or the last 12 months without one. `fresh`: jobs read afresh first (a Stats tab). */
function useStats<T>(kind: string, path: string, id: string, period?: Period, enabled = true, fresh = true) {
    const company = useCompany();
    return useSm8Query<StatsResponse<T>>({
        companyId: company.id,
        objects: [...STATS_OBJECTS],
        key: ["pricelist", "stats", kind, id, period?.from ?? "", period?.to ?? ""],
        path: `/tools/pricelist/${path}${period ? `?from=${period.from}&to=${period.to}` : ""}`,
        fresh,
        enabled,
    });
}

/** How an item is used on jobs (its Stats tab). */
export const useItemStats = (uuid: string, period?: Period, enabled = true) => useStats<ItemStats>("item", `materials/${uuid}/stats`, uuid, period, enabled);
/** A supplier's prices, and what's likely bought from them (its Stats tab). */
export const useSupplierStats = (uuid: string, period?: Period) => useStats<SupplierStats>("supplier", `suppliers/${uuid}/stats`, uuid, period);
/** How a group's items are used on jobs (its Stats tab). */
export const useGroupStats = (id: string, period?: Period) => useStats<GroupStats>("group", `groups/${id}/stats`, id, period);

/**
 * How long an item, or a group's items, take on jobs (its stats' time), for its Labour field: as the jobs are now (no reading them
 * afresh just for this). None without See stats.
 */
export const useItemJobTime = (uuid: string, enabled: boolean) => useStats<ItemStats>("item", `materials/${uuid}/stats`, uuid, undefined, enabled, false).data?.stats.time ?? null;
export const useGroupJobTime = (id: string, enabled: boolean) => useStats<GroupStats>("group", `groups/${id}/stats`, id, undefined, enabled, false).data?.stats.time ?? null;

/**
 * What a draft of the job time settings would give (being tried before it's saved); none without one. `savedAt`: when the saved
 * settings' preview was fetched: worked out again with it (jobs changed), so the two compare like for like.
 */
export function useJobTimeDraftPreview(draft: JobTimeSettings | null, savedAt: number) {
    const company = useCompany();
    return useQuery({
        queryKey: ["c", company.id, "pricelist", "job-time", "draft", draft, savedAt],
        queryFn: () => api<JobTimePreview>(`/c/${company.id}/tools/pricelist/job-time/preview`, { body: { jobTime: draft } }),
        enabled: draft !== null,
        // The last draft's stays while the next is worked out (no blank, no jump).
        placeholderData: keepPreviousData,
    });
}

/** What the job time settings give (Price List settings' preview): every item's time on jobs. */
export function useJobTimePreview(enabled: boolean) {
    const company = useCompany();
    return useSm8Query<JobTimePreview>({
        companyId: company.id,
        objects: [...STATS_OBJECTS, "company"],
        key: ["pricelist", "job-time"],
        path: "/tools/pricelist/job-time",
        enabled,
    });
}

/** An item's tax rate (its own, else the default): the rate, its uuid, and as a fraction. */
export function itemTax(data: Pick<PriceListData, "taxRates">, m: Pick<MaterialRow, "tax_rate_uuid">): ItemRate & { rate: TaxRate | null } {
    const rate = rateFor(m.tax_rate_uuid, data.taxRates);
    return { rate, uuid: rate?.uuid ?? "", tax: fraction(rate) };
}

/**
 * How lists show costs, to compare them: before tax, as a business that claims tax back on purchases
 * pays them, else with it (it's then part of the cost). Not the item's own basis, so a cost reads the
 * same whatever its price includes. (Its window shows it as it was typed: QuotedAmount.tsx.)
 */
/** Costs are shown with tax (the business doesn't claim it on purchases), or without (it does: Price List settings). */
export const costIncludesTax = (data: Pick<PriceListData, "settings">) => !data.settings.purchaseTaxClaimed;

/** An item's cost as it's shown (see costIncludesTax), with its label ("ex GST"). */
export function shownCost(data: Pick<PriceListData, "settings" | "taxRates">, m: Pick<MaterialRow, "cost" | "price_includes_taxes" | "tax_rate_uuid">) {
    const { rate, tax } = itemTax(data, m);
    const inc = costIncludesTax(data);
    return { amount: fromNet(toNet(toNumber(m.cost), m.price_includes_taxes, tax), inc, tax), label: basisLabel(inc, rate), fullLabel: basisLabel(inc, rate, true) };
}

/** A group's tax with its rate as a fraction, and the rate itself (for labels). */
export function groupTaxBasis(data: Pick<PriceListData, "taxRates">, tax: GroupTax): GroupTaxBasis & { rate: TaxRate | null } {
    const rate = rateFor(tax.rateUuid, data.taxRates);
    return { ...tax, rate, tax: fraction(rate) };
}

/** Of these items (a group's, priced by it), how many prices don't match its pricing yet. */
const pricesToUpdate = (data: PriceListData, items: readonly MaterialRow[]) => items.filter((m) => needsCheck(partItem(data, m), itemTax(data, m), data.itemPricing[m.uuid], data.settings)).length;

/** Items in a group that override its price (priced their own way). */
export const overridingIn = (data: PriceListData, groupId: string) => membersOf(data, groupId).filter((m) => data.itemPricing[m.uuid]?.overridesGroup);

/** An item's group, if it's in one. */
export const groupOf = (data: Pick<PriceListData, "itemPricing" | "pricingGroups">, uuid: string) => {
    const id = data.itemPricing[uuid]?.groupId;
    return id ? data.pricingGroups.find((g) => g.id === id) : undefined;
};

/** The group setting an item's name tags: its group, when it sets them (PricingGroupRow setsTags). */
export const tagsGroupOf = (data: PriceListData, uuid: string) => {
    const group = groupOf(data, uuid);
    return group?.setsTags ? group : undefined;
};

/** The group setting an item's pricing: its group, when it sets it (PricingGroupRow setsPricing). */
export const pricingGroupOf = (data: PriceListData, uuid: string) => {
    const group = groupOf(data, uuid);
    return group?.setsPricing ? group : undefined;
};

/** What an item's group sets for it, in a few words: its Group field, and the note where it doesn't set something. */
export function groupSets(group: Pick<PricingGroupRow, "setsPricing" | "setsTags">) {
    if (group.setsPricing && group.setsTags) return "Sets its pricing and name tags.";
    if (group.setsPricing) return "Sets its pricing (not its name tags).";
    if (group.setsTags) return "Sets its name tags (not its pricing).";
    return "Groups it with others: sets neither its pricing nor its name tags.";
}

/** The name its group's name tags give an item, if they read differently in its name now; else null. */
export function groupNameCheck(data: PriceListData, m: Pick<MaterialRow, "uuid" | "name">) {
    const group = tagsGroupOf(data, m.uuid);
    const name = group ? groupTagsName(m.name, group.tags, data.nameTags, data.itemPricing[m.uuid]?.tagOverrides) : null;
    return group && name !== null ? { group, name } : null;
}

/** An item's name as lists show it: the typed name's first line, without its tags. */
export const itemName = (data: Pick<PriceListData, "nameTags">, m: Pick<MaterialRow, "name">) => displayName(m.name, data.nameTags) || "Untitled item";

/** The items in a group. */
export const membersOf = (data: PriceListData, groupId: string) => data.materials.filter((m) => data.itemPricing[m.uuid]?.groupId === groupId);

/**
 * How many of a group's items' prices, and names, don't match it yet; and how many it can't price (no
 * cost: pricing.ts missingCost). Prices only of those it prices (not overriding it); names of all.
 */
export function outOfDate(data: PriceListData, group: PricingGroupRow) {
    const members = membersOf(data, group.id);
    const priced = members.filter((m) => pricedByGroup(data.itemPricing[m.uuid]));
    return {
        prices: pricesToUpdate(data, priced),
        names: group.setsTags ? members.filter((m) => groupTagsName(m.name, group.tags, data.nameTags, data.itemPricing[m.uuid]?.tagOverrides) !== null).length : 0,
        noCost: priced.filter((m) => missingCost(partItem(data, m), data.itemPricing[m.uuid])).length,
        // Any of its items' (its labour, supplier prices or parts moved: supplierPrices.ts costUpdates); none without See costs.
        costs: members.filter((m) => m.uuid in data.costUpdates).length,
    };
}

/** A name for a new group: `name` (its first line), or with a number after it if a group has it already ("Deadbolt 2"). */
export function freeGroupName(data: PriceListData, name: string) {
    const base = name.split(/\r?\n/)[0]?.trim() || "New group";
    const taken = new Set(data.pricingGroups.map((g) => g.name.trim().toLowerCase()));
    let n = 1;
    while (taken.has((n === 1 ? base : `${base} ${n}`).toLowerCase())) n++;
    return n === 1 ? base : `${base} ${n}`;
}

/**
 * Whether it's still showing (a ref, read when a long run ends): a run that outlives it (e.g. a group's updates, its window
 * since switched to another group) doesn't clear what's there now.
 */
export function useStillShown() {
    const shown = useRef(true);
    useEffect(() => {
        shown.current = true;
        return () => {
            shown.current = false;
        };
    }, []);
    return shown;
}

/** Where one item's update has got (updateEach): waiting its turn, being saved, done, or turned down (why). */
export type RowUpdate = { state: "waiting" | "updating" | "done" } | { state: "failed"; error: string };

/**
 * Runs `update` on each item one at a time (each is a ServiceM8 write), telling `onProgress` how
 * far it's got ("Updating 2 of 5 names...") and null once it's done, and `onItem` where each item
 * has got (all waiting first). One turned down doesn't stop the rest: once they're all done, it
 * rejects saying how many couldn't be updated (and the first one's reason).
 */
export async function updateEach(
    uuids: readonly string[],
    update: (uuid: string) => Promise<unknown>,
    what: string,
    onProgress: (text: string | null) => void,
    onItem?: (uuid: string, row: RowUpdate) => void,
) {
    for (const uuid of uuids) onItem?.(uuid, { state: "waiting" });
    const failed: Error[] = [];
    try {
        for (const [i, uuid] of uuids.entries()) {
            onProgress(`Updating ${i + 1} of ${uuids.length} ${what}...`);
            onItem?.(uuid, { state: "updating" });
            try {
                await update(uuid);
                onItem?.(uuid, { state: "done" });
            } catch (err) {
                const e = err instanceof Error ? err : new Error(String(err));
                failed.push(e);
                onItem?.(uuid, { state: "failed", error: e.message });
            }
        }
    } finally {
        onProgress(null);
    }
    const [first] = failed;
    if (first) throw new Error(failed.length === 1 && uuids.length === 1 ? first.message : `${failed.length} of the ${what} couldn't be updated: ${first.message}`);
}

/**
 * Where each item's updates have got (updateEach's `onItem`), by `what:uuid` ("prices:…"): for a list to mark its rows (being
 * saved, waiting, turned down) and drop those done. `track(what)` is the `onItem` for one kind; `clear` forgets them all.
 */
export function useRowUpdates() {
    const [rows, setRows] = useState<ReadonlyMap<string, RowUpdate>>(() => new Map());
    const track = useCallback((what: string) => (uuid: string, row: RowUpdate) => setRows((was) => new Map(was).set(`${what}:${uuid}`, row)), []);
    const clear = useCallback(() => setRows(new Map()), []);
    // Once a run's over: only those turned down are kept (marked till the next run); those done are in the data now.
    const settle = useCallback(() => setRows((was) => new Map([...was].filter(([, row]) => row.state === "failed"))), []);
    return { rows, track, clear, settle };
}

/**
 * Saving. Changes show straight away; saves to one item go one at a time in the order made; the
 * data is refetched once the last save in flight finishes (showing what ServiceM8 stored).
 * Rejections throw, so the field that changed can show why.
 */
export function usePriceListSaver() {
    const company = useCompany();
    const qc = useQueryClient();
    const key = ["c", company.id, "pricelist"];
    const base = `/c/${company.id}/tools/pricelist`;

    /**
     * A save: shown at once (`apply`), sent in order with others to the same `target`, then the
     * records it's about (`saved`, or "all") are fetched fresh once no save is in flight (liveRows.ts).
     */
    async function optimistic<T>(target: string, apply: ((d: PriceListData) => PriceListData) | null, request: () => Promise<T>, saved: Change) {
        return whileSaving(
            qc,
            key,
            async () => {
                if (apply) {
                    await qc.cancelQueries({ queryKey: key, exact: true });
                    const now = qc.getQueryData<PriceListData>(key);
                    if (now) qc.setQueryData(key, apply(now));
                }
                try {
                    return await inOrder(`${company.id}:${target}`, request);
                } finally {
                    queueChange(qc, company.id, saved);
                }
            },
            // The records saved, fresh; and what's shown beside them (histories, stats), which live updates skip while saving.
            () => Promise.all([flushChanges(qc, company.id), qc.invalidateQueries({ queryKey: key, predicate: (q) => q.queryKey.length > key.length })]).then(() => undefined),
        );
    }
    /** A record made here: fetched as soon as it's saved (the live update brings it to everyone else). */
    const made = <T extends { uuid: string }>(kind: "material" | "supplier" | "materialbundle", request: Promise<T>) =>
        request.then((row) => {
            queueChange(qc, company.id, { [kind]: [row.uuid] });
            return row;
        });

    const patchRow = (uuid: string, { nameChange, ...patch }: MaterialUpdate) => (d: PriceListData) => ({
        ...d,
        materials: d.materials.map((m) =>
            m.uuid === uuid ? { ...m, ...patch, ...(nameChange ? { name: applyNameChange(m.name, nameChange, d.nameTags) } : {}) } : m,
        ),
    });

    return {
        updateMaterial: (uuid: string, patch: MaterialUpdate) =>
            // Switching with/without tax rewrites the price and cost on the server: wait for those.
            optimistic(
                uuid,
                patch.price_includes_taxes === undefined ? patchRow(uuid, patch) : null,
                () => api<MaterialRow>(`${base}/materials/${uuid}`, { method: "PATCH", body: patch }),
                { material: [uuid] },
            ),
        /** Sync with ServiceM8 on or off. */
        setSync: (uuid: string, enabled: boolean) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/sync`, { body: { enabled } }), { material: [uuid] }),
        createMaterial: (data: MaterialPatch & { name: string; synced?: boolean }) => made("material", api<MaterialRow>(`${base}/materials`, { body: data })),
        /** A new item like this one (duplicate.ts): the name and item number given, its supplier prices copied too or not. */
        duplicateItem: (uuid: string, opts: { name: string; itemNumber: string; supplierPrices: boolean }) =>
            made("material", api<MaterialRow>(`${base}/materials/${uuid}/duplicate`, { body: opts })),
        /**
         * Save an item's own pricing as edited (its Pricing tab's draft) in one go: the rule (null: a
         * typed price), how it's taxed and the typed price if they changed, and optionally update its
         * price to what the rule gives. The draft stays on screen until the saved data is back.
         */
        /** `confirmLocked`: its price is locked, and they were asked (PriceLock.tsx). */
        saveItemPricing: (uuid: string, draft: { rule: PricingRule | null; tax?: TaxQuote; price?: string; updatePrice?: boolean; confirmLocked?: boolean; overrideGroup?: boolean }) =>
            optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/pricing`, { method: "PUT", body: draft }), { material: [uuid] }),
        /** `confirmLocked`: its price is locked, and they were asked (PriceLock.tsx). */
        updateToPricing: (uuid: string, confirmLocked = false) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/pricing/update`, { body: { confirmLocked } }), { material: [uuid] }),
        /** Its price and its cost to update, in one save (one ServiceM8 write). `confirmLocked` as for its price alone. */
        updatePriceAndCost: (uuid: string, confirmLocked = false) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/price-and-cost/update`, { body: { confirmLocked } }), { material: [uuid] }),
        /** Lock its price (and tax), or unlock it. */
        lockPrice: (uuid: string, locked: boolean) => optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/lock`, { body: { locked } }), { material: [uuid] }),
        /** Its locked price was changed in ServiceM8: put it back as it was... */
        putBackPrice: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/price/put-back`, { body: {} }), { material: [uuid] }),
        /** ...or keep the change. */
        keepPriceChange: (uuid: string) => optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/price/keep-change`, { body: {} }), { material: [uuid] }),
        /** Save its name tags: in a group, those it sets itself (its name updated to them, or left to update); on its own, they're its name. */
        saveItemTags: (uuid: string, own: OwnTags, updateName: boolean) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/tags`, { body: { own, updateName } }), { material: [uuid] }),
        /** In a group: keep its name as it is, the tags that differ becoming its own. */
        keepName: (uuid: string) => optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/name/keep`, { method: "POST" }), { material: [uuid] }),
        /** Its name to the tags it should have (its group's, and those it sets itself). */
        updateNameToGroup: (uuid: string) =>
            optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/name/update-to-group`, { method: "POST" }), { material: [uuid] }),
        /** Someone has seen these records' changes from ServiceM8 (history.ts): they stop showing as changed, straight away. */
        /** `ids`: the changes they were shown (only those are seen). */
        markSeen: (object: TrackedObject, uuids: string[], ids?: string[]) =>
            optimistic(
                `seen:${object}`,
                (d) => {
                    const seen = new Set(uuids);
                    return { ...d, changedInServiceM8: { ...d.changedInServiceM8, [object]: d.changedInServiceM8[object].filter((u) => !seen.has(u)) } };
                },
                () => api<{ ok: true }>(`${base}/history/${object}/seen`, { body: { uuids, ids } }),
                { [object]: uuids },
            ),
        /** These are real items (known.ts): they stop showing as unknown, straight away. */
        markKnown: (uuids: string[]) =>
            optimistic(
                "known",
                (d) => {
                    const marked = new Set(uuids);
                    return { ...d, unknownItems: d.unknownItems.filter((u) => !marked.has(u)) };
                },
                () => api<{ ok: true }>(`${base}/materials/known`, { body: { uuids } }),
                { material: uuids },
            ),
        /** Its "Cost to update": its cost, to what the supplier price it follows gives. */
        updateCost: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/cost/update`, { method: "POST" }), { material: [uuid] }),
        /** Every cost to update (or these items'), in the background: each item updates on screen as it's saved. */
        updateCosts: (uuids?: string[]) => api<{ updating: number }>(`${base}/materials/costs/update`, { body: { uuids } }),
        keepPrice: (uuid: string) => optimistic(uuid, null, () => api<MaterialRow>(`${base}/materials/${uuid}/pricing/keep`, { method: "POST" }), { material: [uuid] }),
        /** In a group: override its price (its own rule, starting from the group's), or back to the group's price. */
        setOverride: (uuid: string, override: boolean) =>
            optimistic(uuid, null, () => api<{ ok: true }>(`${base}/materials/${uuid}/pricing/override`, { body: { override } }), { material: [uuid] }),
        saveSettings: (patch: Partial<PriceListSettings>) =>
            optimistic(
                "settings",
                // Short names merge (only those given change), as the server saves them.
                (d) => ({ ...d, settings: { ...d.settings, ...patch, taxShortNames: patch.taxShortNames ? withShortNames(d.settings.taxShortNames, patch.taxShortNames) : d.settings.taxShortNames } }),
                () => api<PriceListSettings>(`${base}/settings`, { method: "PATCH", body: patch }),
                "all",
            ),
        /**
         * Change the job time settings: `change` is given them as they are by then (with any changes still being saved), so
         * changes made quickly one after another (two items taken out) each keep the other's. What's sent is the whole of
         * them as they are when it goes (after the saves before it).
         */
        saveJobTime: (change: (t: JobTimeSettings) => JobTimeSettings) =>
            optimistic(
                "settings",
                (d) => ({ ...d, settings: { ...d.settings, jobTime: change(d.settings.jobTime) } }),
                () => {
                    const jobTime = qc.getQueryData<PriceListData>(key)?.settings.jobTime;
                    if (!jobTime) throw new Error("The Price List is still loading. Try again in a moment.");
                    return api<PriceListSettings>(`${base}/settings`, { method: "PATCH", body: { jobTime } });
                },
                "all",
            ),
        /** Save the name tags; the item names that read differently now are updated in the background. */
        saveNameTags: (config: NameTagConfig) =>
            api<{ config: NameTagConfig } & NameUpdates>(`${base}/name-tags`, { method: "PUT", body: config }).finally(() => void qc.invalidateQueries({ queryKey: key })),
        /** Update the names that still read differently (e.g. retry ones that failed). */
        updateNames: () => api<NameUpdates>(`${base}/name-tags/update-names`, { body: {} }).finally(() => void qc.invalidateQueries({ queryKey: key })),

        // Suppliers
        createSupplier: (data: SupplierPatch & { name: string; synced?: boolean }) => made("supplier", api<SupplierRow>(`${base}/suppliers`, { body: data })),
        updateSupplier: (uuid: string, patch: SupplierPatch) =>
            optimistic(
                uuid,
                (d) => ({ ...d, suppliers: d.suppliers.map((s) => (s.uuid === uuid ? { ...s, ...patch } : s)) }),
                () => api<SupplierRow>(`${base}/suppliers/${uuid}`, { method: "PATCH", body: patch }),
                { supplier: [uuid] },
            ),
        updateSupplierDetails: (uuid: string, patch: Partial<SupplierDetails>) =>
            optimistic(
                uuid,
                (d) => ({ ...d, suppliers: d.suppliers.map((s) => (s.uuid === uuid ? { ...s, details: { ...s.details, ...patch } } : s)) }),
                () => api<SupplierRow>(`${base}/suppliers/${uuid}/details`, { method: "PATCH", body: patch }),
                // How they usually quote changes how their prices read, across items: everything.
                "all",
            ),
        setSupplierSync: (uuid: string, enabled: boolean) => optimistic(uuid, null, () => api<SupplierRow>(`${base}/suppliers/${uuid}/sync`, { body: { enabled } }), { supplier: [uuid] }),

        // Supplier prices
        addPrice: (price: Omit<SupplierPriceRow, "id" | "checkedAt" | "changedAt">) =>
            optimistic(price.materialUuid, null, () => api<SupplierPriceRow>(`${base}/prices`, { body: price }), { material: [price.materialUuid] }),
        /** `restore`: undoing a change just made, back to when it was last checked and changed. */
        updatePrice: (
            price: SupplierPriceRow,
            patch: Partial<Pick<SupplierPriceRow, "minQty" | "priceNet" | "supplierCode" | "url">> & { quote?: TaxQuote; checked?: boolean; restore?: { checkedAt: number; changedAt: number } },
        ) =>
            optimistic(
                price.materialUuid,
                (d) => ({
                    ...d,
                    supplierPrices: d.supplierPrices.map((p) => {
                        if (p.id !== price.id) return p;
                        const { restore, checked, ...fields } = patch;
                        return { ...p, ...fields, ...(checked ? { checkedAt: Date.now() } : {}), ...restore };
                    }),
                }),
                () => api<SupplierPriceRow>(`${base}/prices/${price.id}`, { method: "PATCH", body: patch }),
                { material: [price.materialUuid] },
            ),
        removePrice: (price: SupplierPriceRow) =>
            optimistic(
                price.materialUuid,
                (d) => ({ ...d, supplierPrices: d.supplierPrices.filter((p) => p.id !== price.id) }),
                () => api(`${base}/prices/${price.id}`, { method: "DELETE" }),
                { material: [price.materialUuid] },
            ),
        /**
         * A typed cost: the amount exactly as typed, and how it's taxed. Shown at once with the cost
         * that gives (`cost`, as ServiceM8 stores it), so the amount never flickers (and is shown back
         * exactly as typed: supplierPricing.ts shownTypedCost); the server works the cost out itself.
         */
        saveTypedCost: (materialUuid: string, typed: string, quote: TaxQuote, cost: string, update = true) =>
            optimistic(
                materialUuid,
                (d) => ({
                    ...d,
                    costQuotes: { ...d.costQuotes, [materialUuid]: quote },
                    costTyped: { ...d.costTyped, [materialUuid]: typed },
                    // Saved only: ServiceM8's cost stays, and the typed one's a cost to update (when it gives another).
                    ...(update
                        ? { materials: d.materials.map((m) => (m.uuid === materialUuid ? { ...m, cost } : m)) }
                        : samePrice(cost, d.materials.find((m) => m.uuid === materialUuid)?.cost ?? cost)
                          ? {}
                          : { costUpdates: { ...d.costUpdates, [materialUuid]: cost } }),
                }),
                () => api<MaterialRow>(`${base}/materials/${materialUuid}/typed-cost`, { method: "PUT", body: { ...quote, typed, update } }),
                { material: [materialUuid] },
            ),
        /** A kit's parts, all at once, in order (kits.ts). */
        setKitParts: (materialUuid: string, parts: KitPart[]) =>
            optimistic(
                materialUuid,
                (d) => ({ ...d, kitParts: { ...d.kitParts, [materialUuid]: parts } }),
                () => api(`${base}/materials/${materialUuid}/kit-parts`, { method: "PUT", body: { parts } }),
                { material: [materialUuid] },
            ),
        setCostSource: (materialUuid: string, source: CostSource) =>
            optimistic(
                materialUuid,
                (d) => withCostSource(d, materialUuid, source),
                () => api(`${base}/materials/${materialUuid}/cost-source`, { method: "PUT", body: source }),
                { material: [materialUuid] },
            ),
        // Groups
        createGroup: (name: string, rule: PricingRule, materialUuid?: string) =>
            optimistic(materialUuid ?? "groups", null, () => api<PricingGroupRow>(`${base}/groups`, { body: { name, rule, materialUuid } }), "all"),
        updateGroup: (
            id: string,
            patch: { name?: string; description?: string; rule?: Partial<PricingRule>; tax?: GroupTax; tags?: GroupTags; locksPrices?: boolean; setsPricing?: boolean; setsTags?: boolean },
        ) =>
            optimistic(
                `group:${id}`,
                (d) => ({
                    ...d,
                    pricingGroups: d.pricingGroups.map((g) =>
                        g.id === id
                            ? {
                                  ...g,
                                  name: patch.name ?? g.name,
                                  description: patch.description ?? g.description,
                                  rule: { ...g.rule, ...patch.rule },
                                  tax: patch.tax ?? g.tax,
                                  tags: patch.tags ?? g.tags,
                                  locksPrices: patch.locksPrices ?? g.locksPrices,
                                  setsPricing: patch.setsPricing ?? g.setsPricing,
                                  setsTags: patch.setsTags ?? g.setsTags,
                              }
                            : g,
                    ),
                }),
                () => api(`${base}/groups/${id}`, { method: "PATCH", body: patch }),
                "all",
            ),
        deleteGroup: (id: string) => optimistic(`group:${id}`, null, () => api(`${base}/groups/${id}`, { method: "DELETE" }), "all"),
        joinGroup: (uuid: string, groupId: string | null) =>
            optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group`, { method: "PUT", body: { groupId } }), "all"),
        /** Out of its group in one step, keeping its price (`keepPrice`: the group gives another). */
        takeOutOfGroup: (uuid: string, keepPrice: boolean) => optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group/take-out`, { body: { keepPrice } }), "all"),
        /** Back as it was in one step (groups.ts rejoinGroup): in its group (or none), with `overrideWith` its own rule, or (null) without one. */
        rejoinGroup: (uuid: string, groupId: string | null, overrideWith: PricingRule | null) =>
            optimistic(uuid, null, () => api(`${base}/materials/${uuid}/group/rejoin`, { body: { groupId, overrideWith } }), "all"),

        // Bundles
        createBundle: (data: { name: string; item_number: string; material_list?: BundleLine[]; synced?: boolean }) =>
            made("materialbundle", api<BundleRow>(`${base}/bundles`, { body: data })),
        updateBundle: (uuid: string, patch: Partial<Pick<BundleRow, "name" | "item_number" | "material_list" | "active">>) =>
            optimistic(
                uuid,
                (d) => ({ ...d, bundles: d.bundles.map((b) => (b.uuid === uuid ? { ...b, ...patch } : b)) }),
                () => api<BundleRow>(`${base}/bundles/${uuid}`, { method: "PATCH", body: patch }),
                { materialbundle: [uuid] },
            ),
        setBundleSync: (uuid: string, enabled: boolean) => optimistic(uuid, null, () => api<BundleRow>(`${base}/bundles/${uuid}/sync`, { body: { enabled } }), { materialbundle: [uuid] }),

        setHistoryIgnored: (priceId: string, entryId: string, ignored: boolean) =>
            api(`${base}/prices/${priceId}/history/${entryId}`, { method: "PATCH", body: { ignored } }).finally(
                () => void qc.invalidateQueries({ queryKey: [...key, "history", priceId] }),
            ),
    };
}

/** A supplier price's change history, newest first. Loaded when it's opened. */
export function usePriceHistory(priceId: string) {
    const company = useCompany();
    return useQuery({
        queryKey: ["c", company.id, "pricelist", "history", priceId],
        queryFn: () => api<PriceHistoryEntry[]>(`/c/${company.id}/tools/pricelist/prices/${priceId}/history`),
    });
}

/** How a quote is taxed: its rate, and that as a fraction. */
export function quoteRate(data: Pick<PriceListData, "taxRates">, quote: TaxQuote) {
    const rate = rateFor(quote.rateUuid, data.taxRates);
    return { rate, tax: fraction(rate) };
}

/** How a supplier price is quoted (its own way, else as its supplier usually quotes), with its rate and that as a fraction. */
export function priceQuote(data: Pick<PriceListData, "taxRates">, price: Pick<SupplierPriceRow, "quote">, supplier: Pick<SupplierRow, "details"> | undefined) {
    const quote = quoteOf(price, supplier?.details);
    return { quote, ...quoteRate(data, quote) };
}

/** How an item's typed cost is typed: as last typed, else before tax at its own rate. */
export function costQuote(data: Pick<PriceListData, "taxRates" | "costQuotes">, m: Pick<MaterialRow, "uuid" | "tax_rate_uuid">): TaxQuote {
    return data.costQuotes[m.uuid] ?? { rateUuid: itemTax(data, m).uuid, includesTax: false };
}

/** Where an item's cost comes from, as the data has it. */
function costSourceOf(data: Pick<PriceListData, "costFollows" | "costPrices" | "kitParts">, materialUuid: string): CostSource {
    if (materialUuid in data.kitParts) return { from: "parts" };
    if (!data.costFollows.includes(materialUuid)) return { from: "typed" };
    const priceId = data.costPrices[materialUuid];
    return priceId ? { from: "price", priceId } : { from: "cheapest" };
}

/** The data with an item's cost coming from `source` (saved, or a draft's: what its Costing tab shows while it's edited). */
export function withCostSource(d: PriceListData, materialUuid: string, source: CostSource): PriceListData {
    const others = d.costFollows.filter((u) => u !== materialUuid);
    const costPrices = Object.fromEntries(Object.entries(d.costPrices).filter(([u]) => u !== materialUuid));
    // A kit keeps its parts (back to one, they're there): only whether it's a kit changes.
    const { [materialUuid]: parts, ...kits } = d.kitParts;
    return {
        ...d,
        costFollows: source.from === "typed" || source.from === "parts" ? others : [...others, materialUuid],
        costPrices: source.from === "price" ? { ...costPrices, [materialUuid]: source.priceId } : costPrices,
        kitParts: source.from === "parts" ? { ...kits, [materialUuid]: parts ?? [] } : kits,
    };
}

/**
 * An item's cost being changed, not saved yet (its Costing tab: kept by its window, as its pricing's draft): where it comes from,
 * and (typed in) what's typed, as quoted. Saved by its save bar: Save & update (its cost too), Save only, or (typed) Save.
 */
export interface CostDraft {
    source: CostSource;
    typed?: { amount: string; quote: TaxQuote };
}

/**
 * The part cost a cost draft gives, in the item's basis: as typed, a kit's parts added up (none yet: nothing), or the supplier
 * price it would follow (as the server works it out: supplierPrices.ts partCostNow); else its part's as it is.
 */
export function draftPartCost(data: PriceListData, item: MaterialRow, draft: CostDraft): string {
    const { tax } = itemTax(data, item);
    if (draft.source.from === "typed") {
        if (!draft.typed) return partItem(data, item).cost;
        const { amount, quote } = draft.typed;
        return costFromQuoted(amount, quote, quoteRate(data, quote).tax, item, tax, data.settings);
    }
    const view = withCostSource(data, item.uuid, draft.source);
    if (draft.source.from === "parts") return kitCostOf(view, item) ?? "0.00";
    const pick = itemSupplierPrices(view, item.uuid).cost?.pick;
    return pick ? itemCost(pick.real, item, tax) : partItem(data, item).cost;
}

/** A kit's part cost from its parts as the data has them (pricing.ts kitCost: each part's own; none yet: nothing), or null: not a kit. */
export function kitCostOf(data: Pick<PriceListData, "kitParts" | "materials" | "taxRates" | "labourInCost">, kit: MaterialRow): string | null {
    const parts = data.kitParts[kit.uuid];
    if (!parts) return null;
    const items = new Map(data.materials.map((m) => [m.uuid, m]));
    return kitCost(kit, parts, (uuid) => items.get(uuid), data);
}

/** A kit's parts' own prices added up, before tax, as the data has them (pricing.ts kitPartsPrice), or null: not a kit, or no parts yet. */
export function partsPriceOf(data: Pick<PriceListData, "kitParts" | "materials" | "taxRates">, uuid: string): string | null {
    const parts = data.kitParts[uuid];
    if (!parts) return null;
    const items = new Map(data.materials.map((m) => [m.uuid, m]));
    return kitPartsPrice(parts, (u) => items.get(u), data.taxRates);
}

/** The kits an item is a part of. */
export const kitsWith = (data: Pick<PriceListData, "kitParts" | "materials">, partUuid: string) =>
    data.materials.filter((m) => data.kitParts[m.uuid]?.some((p) => p.partUuid === partUuid));

/** An item's supplier prices, cheapest real cost first, and (when its cost follows one) the price it follows and why. */
export function itemSupplierPrices(data: PriceListData, materialUuid: string) {
    const suppliers = new Map(data.suppliers.map((s) => [s.uuid, s]));
    const ranked = byRealCost(
        data.supplierPrices.filter((p) => p.materialUuid === materialUuid),
        (p) => priceQuote(data, p, suppliers.get(p.supplierUuid)).tax,
        data.settings,
    );
    const source = costSourceOf(data, materialUuid);
    const chosen = source.from === "price" ? source.priceId : null;
    const cost = source.from === "typed" || source.from === "parts" ? null : costPrice(ranked, chosen);
    return { ranked, source, cost };
}
export type PriceListSaver = ReturnType<typeof usePriceListSaver>;

/**
 * An item's cost as its Costing tab shows it: the supplier price it follows exactly as quoted (and
 * which supplier), or its typed cost exactly as typed. Never read back from ServiceM8's cost, which
 * is kept to the cent in the item's own basis. The part's own cost (pricing.ts partItem). The Pricing tab shows the same.
 */
export function itemCostShown(data: PriceListData, item: MaterialRow): { amount: string; quote: TaxQuote; from: string | null } {
    // A kit: its parts added up (in its own basis), else (none yet) the cost it has.
    if (item.uuid in data.kitParts) {
        return { amount: kitCostOf(data, item) ?? toCents(toNumber(partItem(data, item).cost)), quote: { rateUuid: itemTax(data, item).uuid, includesTax: item.price_includes_taxes }, from: "its parts" };
    }
    const pick = itemSupplierPrices(data, item.uuid).cost?.pick;
    if (pick) {
        const supplier = data.suppliers.find((x) => x.uuid === pick.price.supplierUuid);
        const followed = priceQuote(data, pick.price, supplier);
        return { amount: quotedPrice(pick.price.priceNet, followed.quote, followed.tax), quote: followed.quote, from: supplier?.name ?? "a supplier" };
    }
    const quote = costQuote(data, item);
    const { tax } = itemTax(data, item);
    const typed = data.costTyped[item.uuid] ?? null;
    // Saved only (waiting: its cost to update is what it gives): as typed, not ServiceM8's.
    const waiting = data.costUpdates[item.uuid];
    if (typed !== null && waiting !== undefined && costWithLabour(data, item, costFromQuoted(typed, quote, quoteRate(data, quote).tax, item, tax, data.settings)) === waiting) {
        return { amount: toCents(toNumber(typed)), quote, from: null };
    }
    const amount = shownTypedCost(partItem(data, item), tax, quote, quoteRate(data, quote).tax, data.settings, typed);
    return { amount, quote, from: null };
}
